import React, { useState, useEffect, useRef, useCallback } from 'react';

export const AsyncMultiSelect = ({ label, placeholder, onSelectionChange, fetchOptions, isDisabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);

    const [selection, setSelection] = useState({
        isSelectAllGlobal: false,
        selectAllFilters: new Set(),
        selectedItems: new Map(),
        excludedItems: new Map()
    });
    
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const scrollContainerRef = useRef(null);
    const wrapperRef = useRef(null);
    
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchText(searchText);
            setOffset(0);
        }, 300);
        return () => clearTimeout(timerId);
    }, [searchText]);

    useEffect(() => {
        if (typeof fetchOptions !== 'function') {
            console.error("The 'fetchOptions' prop must be a function.");
            return;
        }
        if (!isOpen) return;

        const fetchData = async () => {
            if (!hasMore && offset > 0) return;
            setIsLoading(true);
            const { items: newItems, hasMore: newHasMore } = await fetchOptions({
                offset, limit: 15, query: debouncedSearchText
            });
            setItems(prevItems => (offset === 0 ? newItems : [...prevItems, ...newItems]));
            setHasMore(newHasMore);
            setIsLoading(false);
        };
        fetchData();
    }, [debouncedSearchText, offset, fetchOptions, isOpen, hasMore]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (container) {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < 20 && hasMore && !isLoading) {
                setOffset(prevOffset => prevOffset + 15);
            }
        }
    }, [hasMore, isLoading]);

    const handleSelectAll = () => {
        const newSelection = {
            isSelectAllGlobal: selection.isSelectAllGlobal,
            selectAllFilters: new Set(selection.selectAllFilters),
            selectedItems: new Map(selection.selectedItems),
            excludedItems: new Map(selection.excludedItems)
        };

        if (!debouncedSearchText) {
            const newIsSelectAllGlobal = !selection.isSelectAllGlobal;
            newSelection.isSelectAllGlobal = newIsSelectAllGlobal;
            newSelection.selectAllFilters = new Set();
            newSelection.selectedItems = new Map();
            newSelection.excludedItems = new Map();
        } else {
            if (selection.isSelectAllGlobal) {
                const areAllVisibleExcluded = items.every(item => newSelection.excludedItems.has(item.Key));
                
                if (areAllVisibleExcluded) {
                    items.forEach(item => newSelection.excludedItems.delete(item.Key));
                } else {
                    items.forEach(item => newSelection.excludedItems.set(item.Key, item.Value));
                }
            } else {
                if (newSelection.selectAllFilters.has(debouncedSearchText)) {
                    newSelection.selectAllFilters.delete(debouncedSearchText);
                } else {
                    newSelection.selectAllFilters.add(debouncedSearchText);
                }
            }
        }
        
        setSelection(newSelection);
        propagateSelectionChange(newSelection);
    };
    
    const handleOptionClick = (option) => {
        const newSelection = {
            isSelectAllGlobal: selection.isSelectAllGlobal,
            selectAllFilters: new Set(selection.selectAllFilters),
            selectedItems: new Map(selection.selectedItems),
            excludedItems: new Map(selection.excludedItems)
        };

        const coveringFilter = Array.from(newSelection.selectAllFilters).find(filter => 
            option.Value.toLowerCase().startsWith(filter.toLowerCase())
        );

        if (newSelection.isSelectAllGlobal || coveringFilter) {
            if (newSelection.excludedItems.has(option.Key)) {
                newSelection.excludedItems.delete(option.Key);
            } else {
                newSelection.excludedItems.set(option.Key, option.Value);
            }
        } else {
            if (newSelection.selectedItems.has(option.Key)) {
                newSelection.selectedItems.delete(option.Key);
            } else {
                newSelection.selectedItems.set(option.Key, option.Value);
            }
        }
        
        setSelection(newSelection);
        propagateSelectionChange(newSelection);
    };
    
    const propagateSelectionChange = (currentSelection) => {
        if (typeof onSelectionChange === 'function') {
            onSelectionChange({
                isSelectAllGlobal: currentSelection.isSelectAllGlobal,
                selectAllFilters: Array.from(currentSelection.selectAllFilters),
                selectedKeys: Array.from(currentSelection.selectedItems.keys()),
                excludedKeys: Array.from(currentSelection.excludedItems.keys())
            });
        }
    };

    const getDisplayText = () => {
        const { isSelectAllGlobal, selectAllFilters, selectedItems, excludedItems } = selection;
        
        if (isSelectAllGlobal) {
            let text = "All items selected";
            if (excludedItems.size > 0) {
                text += ` (except ${excludedItems.size})`;
            }
            return text;
        }

        if (selectAllFilters.size === 0 && selectedItems.size === 0) return '';
        if (selectAllFilters.size > 0) return `${selectAllFilters.size} filter(s) and ${selectedItems.size} item(s) selected`;
        return `${selectedItems.size} item(s) selected`;
    };

    let tooltipContent = [];
    if (selection.isSelectAllGlobal) {
        tooltipContent.push("All items are selected.");
    }
    if (selection.selectAllFilters.size > 0) {
        tooltipContent.push(`Filters Active: ${Array.from(selection.selectAllFilters).join(', ')}`);
    }
    if (selection.selectedItems.size > 0) {
        tooltipContent.push(`Individually Selected: ${Array.from(selection.selectedItems.values()).join('; ')}`);
    }
    if (selection.excludedItems.size > 0) {
        tooltipContent.push(`Excluded: ${Array.from(selection.excludedItems.values()).join('; ')}`);
    }
    
    const showTooltip = isTooltipVisible && (
        selection.isSelectAllGlobal ||
        selection.selectAllFilters.size > 0 || 
        selection.selectedItems.size > 0
    );

    let isSelectAllChecked;
    if (debouncedSearchText) {
        if (selection.isSelectAllGlobal) {
            isSelectAllChecked = items.length > 0 && !items.some(item => selection.excludedItems.has(item.Key));
        } else {
            isSelectAllChecked = selection.selectAllFilters.has(debouncedSearchText);
        }
    } else {
        isSelectAllChecked = selection.isSelectAllGlobal;
    }

    return (
        <div className="multi-select__container" ref={wrapperRef}>
            {showTooltip && !isOpen && tooltipContent.length > 0 && (
                <div className="custom-tool-tip__container">
                    {tooltipContent.map((line, index) => <div key={index}>{line}</div>)}
                </div>
            )}
            <div className={`custom-select-wrapper ${isOpen ? 'border-blue' : ''} ${isDisabled ? 'disabled' : ''}`}>
                <div className="dropdown-header"
                    onMouseEnter={() => !isDisabled && setIsTooltipVisible(true)}
                    onMouseLeave={() => setIsTooltipVisible(false)}>
                    <div className="custom-select-label">{label}</div>
                    <input
                        type="text"
                        className="custom-select-input form-select"
                        placeholder={!isDisabled ? placeholder : ''}
                        value={isOpen ? searchText : getDisplayText()}
                        onChange={(e) => setSearchText(e.target.value)}
                        onFocus={() => !isDisabled && setIsOpen(true)}
                        disabled={isDisabled}
                        autoComplete="off"
                    />
                </div>
                {isOpen && !isDisabled && (
                    <div className="custom-select-options" ref={scrollContainerRef} onScroll={handleScroll}>
                        {items.length > 0 && (
                            <div className="dropdown-option" onMouseDown={(e) => { e.preventDefault(); handleSelectAll(); }}>
                                <input 
                                    type="checkbox" 
                                    readOnly 
                                    checked={isSelectAllChecked}
                                    className="checkbox__selection" 
                                />
                                Select All {debouncedSearchText && `(matching "${debouncedSearchText}")`}
                            </div>
                        )}
                        {items.map(option => {
                            const coveringFilter = Array.from(selection.selectAllFilters).find(filter => 
                                option.Value.toLowerCase().startsWith(filter.toLowerCase())
                            );
                            const isChecked = 
                                (selection.isSelectAllGlobal && !selection.excludedItems.has(option.Key)) ||
                                (coveringFilter && !selection.excludedItems.has(option.Key)) || 
                                selection.selectedItems.has(option.Key);

                            return (
                                <div key={option.Key} className="dropdown-option" onMouseDown={(e) => { e.preventDefault(); handleOptionClick(option); }}>
                                    <input
                                        type="checkbox"
                                        readOnly
                                        checked={isChecked}
                                        className="checkbox__selection"
                                    />
                                    {option.Value}
                                </div>
                            );
                        })}
                        {isLoading && <div className="dropdown-option">Loading...</div>}
                        {!hasMore && items.length > 0 && <div className="dropdown-option">End of results.</div>}
                        {!isLoading && items.length === 0 && <div className="dropdown-option">No results found.</div>}
                    </div>
                )}
            </div>
        </div>
    );
};